import { useState } from 'react';
import { Box, TextField, Typography, Stack, InputAdornment, Collapse } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import RightArrowIcon from '@mui/icons-material/ArrowRight';


const Sidebar = ({ activeSection, onSectionChange }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedSections, setExpandedSections] = useState({
        introduction: true,
        authentication: true,
        order: true,
        'place-order': false
    });

    const sections = [
        {
            id: 'introduction',
            title: 'Introduction',
            icon: '📘',
            items: []
        },
        {
            id: 'authentication',
            title: 'Authentication',
            icon: '🔐',
            items: [
                { id: 'login', title: 'Login', method: 'POST' },
                { id: 'signup', title: 'Signup', method: 'POST' }
            ]
        },
        {
            id: 'domain-search',
            title: 'Domain Search',
            icon: '🔍',
            items: [
                { id: 'domain-suggestions', title: 'Domain Suggestions', method: 'GET' },
                { id: 'bulk-check', title: 'Bulk Availability', method: 'GET' }
            ]
        },
        {
            id: 'domain-mgmt',
            title: 'Domain Management',
            icon: '🌐',
            items: [
                {
                    id: 'dns-records',
                    title: 'DNS Records',
                    subItems: [
                        { id: 'get-dns', title: 'Get DNS Records', method: 'GET' },
                        { id: 'update-dns', title: 'Update DNS Records', method: 'POST' }
                    ]
                },
                {
                    id: 'domain-forwarding',
                    title: 'Domain Forwarding',
                    subItems: [
                        { id: 'create-redirect', title: 'Create Redirect', method: 'POST' },
                        { id: 'delete-redirect', title: 'Delete Redirect', method: 'DELETE' }
                    ]
                },
                { id: 'renewal-pricing', title: 'Renewal Pricing', method: 'GET' }
            ]
        },
        {
            id: 'order',
            title: 'Order Management',
            icon: '📦',
            items: [
                { id: 'place-order', title: 'Place Order', method: 'POST' },
                { id: 'config-status', title: 'Domain Config Status', method: 'GET' },
                { id: 'add-mailbox', title: 'Add Mailbox', method: 'POST' },
                { id: 'delete-mailbox', title: 'Delete Mailbox', method: 'POST' },
                { id: 'update-from-name', title: 'Update From Name', method: 'PUT' },
                { id: 'update-profile-pic', title: 'Update Profile Picture', method: 'PUT' },
                { id: 'mailbox-credentials', title: 'Mailbox Credentials', method: 'GET' },
                { id: 'order-status-check', title: 'Order Status Check', method: 'GET' },
                { id: 'view-flatten-order', title: 'View Flatten Order', method: 'GET' }
            ]
        },
        {
            id: 'email-accounts',
            title: 'Email Account Management',
            icon: '✉️',
            items: [
                // { id: 'email-forwarding', title: 'Email Forwarding', method: 'NOTE' },
                // { id: 'oauth2-integration', title: 'OAuth2 Integration', method: 'NOTE' },
                { id: 'cancelsubscription', title: 'Cancel Subscription', method: 'POST' }
            ]
        }
    ];

    const getMethodColor = (method) => {
        const colors = {
            'GET': '#3b82f6',
            'POST': '#f97316',
            'PUT': '#8b5cf6',
            'DELETE': '#ef4444',
            'NOTE': '#9ca3af'
        };
        return colors[method] || '#6b7280';
    };

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const filteredSections = sections.map(section => ({
        ...section,
        items: section.items.filter(item => {
            const itemMatches = item.title.toLowerCase().includes(searchTerm.toLowerCase());
            const subItemMatches = item.subItems?.some(subItem =>
                subItem.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
            return itemMatches || subItemMatches;
        }).map(item => ({
            ...item,
            subItems: item.subItems?.filter(subItem =>
                subItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.title.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }))
    })).filter(section =>
        section.items.length > 0 || section.id === 'introduction'
    );

    return (
        <Box
            sx={{
                width: 300,
                height: '100vh',
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                borderRight: '1px solid #e5e7eb',
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: 1000,
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 800,
                        color: '#1f2937',
                        mb: 2.5,
                        letterSpacing: '-0.5px'
                    }}
                >
                    Inboxlogy
                </Typography>

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search endpoints..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            '& fieldset': { borderColor: '#d1d5db' },
                            '&:hover fieldset': { borderColor: '#9ca3af' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                            fontSize: '0.9rem'
                        }
                    }}
                />
            </Box>

            {/* Navigation */}
            <Box sx={{ flex: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: '6px' } }}>
                <Stack spacing={0.5} sx={{ p: 1 }}>
                    {filteredSections.map((section) => (
                        <Box key={section.id}>
                            <Box
                                onClick={() => {
                                    if (section.items.length === 0) {
                                        onSectionChange(section.id);
                                    } else {
                                        toggleSection(section.id);
                                    }
                                }}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '8px',
                                    backgroundColor: activeSection === section.id ? '#eff6ff' : 'transparent',
                                    '&:hover': { backgroundColor: '#f3f4f6' },
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        color: activeSection === section.id ? '#1e40af' : '#374151',
                                        fontSize: '0.95rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5
                                    }}
                                >
                                    <span>{section.icon}</span>
                                    {section.title}
                                </Typography>
                                {section.items.length > 0 && (
                                    <Box sx={{ color: '#9ca3af' }}>
                                        {expandedSections[section.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </Box>
                                )}
                            </Box>

                            {section.items.length > 0 && (
                                <Collapse in={expandedSections[section.id]}>
                                    <Stack spacing={0.5} sx={{ ml: 2, mt: 0.5 }}>
                                        {section.items.map((item) => (
                                            <Box key={item.id}>
                                                <Box
                                                    onClick={() => {
                                                        if (item.subItems && item.subItems.length > 0) {
                                                            toggleSection(item.id);
                                                        } else {
                                                            onSectionChange(item.id);
                                                        }
                                                    }}
                                                    sx={{
                                                        px: 2,
                                                        py: 1,
                                                        cursor: 'pointer',
                                                        borderRadius: '6px',
                                                        backgroundColor: activeSection === item.id ? '#dbeafe' : 'transparent',
                                                        '&:hover': { backgroundColor: '#f0f9ff' },
                                                        transition: 'all 0.2s',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                        {item.subItems && item.subItems.length > 0 ? (
                                                            <Box sx={{ color: '#9ca3af' }}>
                                                                {expandedSections[item.id] ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                                            </Box>
                                                        ) : (
                                                            <RightArrowIcon sx={{ fontSize: 18, color: '#d1d5db' }} />
                                                        )}
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.9rem',
                                                                fontWeight: 500,
                                                                color: activeSection === item.id ? '#1e40af' : '#4b5563'
                                                            }}
                                                        >
                                                            {item.title}
                                                        </Typography>
                                                    </Box>
                                                    {item.method && (
                                                        <Box
                                                            sx={{
                                                                px: 1.5,
                                                                py: 0.3,
                                                                backgroundColor: getMethodColor(item.method),
                                                                color: 'white',
                                                                borderRadius: '4px',
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                                minWidth: '45px',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {item.method}
                                                        </Box>
                                                    )}
                                                </Box>

                                                {/* Render sub-items if they exist */}
                                                {item.subItems && item.subItems.length > 0 && (
                                                    <Collapse in={expandedSections[item.id]}>
                                                        <Stack spacing={0.5} sx={{ ml: 3, mt: 0.5 }}>
                                                            {item.subItems.map((subItem) => (
                                                                <Box
                                                                    key={subItem.id}
                                                                    onClick={() => onSectionChange(subItem.id)}
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 1,
                                                                        cursor: 'pointer',
                                                                        borderRadius: '6px',
                                                                        backgroundColor: activeSection === subItem.id ? '#bfdbfe' : 'transparent',
                                                                        '&:hover': { backgroundColor: '#dbeafe' },
                                                                        transition: 'all 0.2s',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        gap: 1
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                                        <RightArrowIcon sx={{ fontSize: 16, color: '#d1d5db' }} />
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: '0.85rem',
                                                                                fontWeight: 500,
                                                                                color: activeSection === subItem.id ? '#1e40af' : '#6b7280'
                                                                            }}
                                                                        >
                                                                            {subItem.title}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box
                                                                        sx={{
                                                                            px: 1.5,
                                                                            py: 0.3,
                                                                            backgroundColor: getMethodColor(subItem.method),
                                                                            color: 'white',
                                                                            borderRadius: '4px',
                                                                            fontSize: '0.65rem',
                                                                            fontWeight: 700,
                                                                            minWidth: '42px',
                                                                            textAlign: 'center'
                                                                        }}
                                                                    >
                                                                        {subItem.method}
                                                                    </Box>
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    </Collapse>
                                                )}
                                            </Box>
                                        ))}
                                    </Stack>
                                </Collapse>
                            )}
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
};

export default Sidebar;